import { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import { TeachingBanner } from "../components/TeachingBanner";

const COUNTRIES = [
  "United States", "United Kingdom", "Canada", "Pakistan", "India",
  "Australia", "Germany", "France", "United Arab Emirates", "Other",
];

interface Profile {
  country?: string;
  heritage?: string;
  religion?: string;
  professional_role?: string;
  has_kids?: boolean;
  has_parents_alive?: boolean;
}

/** /settings/profile/cultural - writes user_settings cultural.* keys
 *  read by the cultural-calendar-enricher worker. */
export function CulturalProfile() {
  const [p, setP] = useState<Profile>({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch("/api/cultural-profile", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { profile: {} }))
      .then((j) => setP(j.profile ?? {}))
      .catch(() => setP({}))
      .finally(() => setLoading(false));
  }, []);

  const csrf = (): Record<string, string> => {
    const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
    return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
  };

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      await fetch("/api/cultural-profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...csrf() },
        credentials: "same-origin",
        body: JSON.stringify(p),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setBusy(false);
    }
  };

  const set = (k: keyof Profile, v: unknown) => setP((x) => ({ ...x, [k]: v }));

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <header className="space-y-1">
        <div className="text-xs uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
          Settings
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Cultural profile
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Helps Memso surface the right cultural observances and family
          context. All fields optional.
        </p>
      </header>

      <TeachingBanner id="cultural_profile_overview" />

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-5 space-y-4">
          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
            Country
            <select
              value={p.country ?? ""}
              onChange={(e) => set("country", e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm font-normal"
            >
              <option value="">Select…</option>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          {(
            [
              ["heritage", "Heritage"],
              ["religion", "Religion"],
              ["professional_role", "Professional role"],
            ] as const
          ).map(([k, label]) => (
            <label
              key={k}
              className="block text-sm font-semibold text-slate-700 dark:text-slate-200"
            >
              {label}
              <input
                value={(p[k] as string) ?? ""}
                onChange={(e) => set(k, e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm font-normal"
              />
            </label>
          ))}
          <div className="flex flex-col gap-2">
            {(
              [
                ["has_kids", "I have kids"],
                ["has_parents_alive", "My parents are alive"],
              ] as const
            ).map(([k, label]) => (
              <label
                key={k}
                className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
              >
                <input
                  type="checkbox"
                  checked={!!p[k]}
                  onChange={(e) => set(k, e.target.checked)}
                />
                {label}
              </label>
            ))}
          </div>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : saved ? (
              <Check className="w-4 h-4" />
            ) : null}
            {saved ? "Saved" : "Save profile"}
          </button>
        </div>
      )}
    </div>
  );
}
